import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, map, Observable, of, switchMap, tap } from 'rxjs';
import { TestRunService } from './test-run.service';
import type { RunTarget } from './execution-job.service';
import { StartedTestRunExecution, TestRun } from '../models/test-run.model';
import {
  StartRunnerRoundDialogComponent,
  type StartRunnerRoundDialogData,
} from '../dashboard/testlauf-durchlaeufe/start-runner-round-dialog/start-runner-round-dialog.component';
import { testlaufStartFehler } from '../dashboard/testlauf-durchlaeufe/testlauf-start-fehler';

const MELDUNG_MS = 4000;

@Injectable({ providedIn: 'root' })
export class TestlaufOeffnenService {
  private readonly runService = inject(TestRunService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  oeffnen(projectId: string, runId: string): Observable<void> {
    const durchlaeufe = ['/projects', projectId, 'test-runs', runId, 'executions'];
    return this.runService.listExecutions(projectId, runId).pipe(
      switchMap((liste) => {
        const offen = liste.find((durchlauf) => durchlauf.live);
        if (offen) {
          this.router.navigate([...durchlaeufe, offen.id]);
          return EMPTY;
        }
        return this.runService.get(projectId, runId).pipe(
          switchMap((lauf) => this.zielWaehlen(projectId, lauf)),
          catchError(() => this.ladeFehler()),
          switchMap((target) => (target === undefined ? EMPTY : this.starten(projectId, runId, target, durchlaeufe))),
        );
      }),
      catchError(() => this.ladeFehler()),
      map(() => undefined),
    );
  }

  /** `null`: kein Ziel noetig; `undefined`: Auswahl abgebrochen. */
  private zielWaehlen(projectId: string, lauf: TestRun): Observable<RunTarget | null | undefined> {
    if (lauf.source !== 'TCDL') return of(null);
    return this.dialog
      .open<StartRunnerRoundDialogComponent, StartRunnerRoundDialogData, RunTarget>(StartRunnerRoundDialogComponent, {
        data: { projectId, runKey: lauf.key, runName: lauf.name },
        autoFocus: 'dialog',
      })
      .afterClosed();
  }

  private starten(
    projectId: string,
    runId: string,
    target: RunTarget | null,
    durchlaeufe: string[],
  ): Observable<StartedTestRunExecution> {
    const start = target
      ? this.runService.startExecution(projectId, runId, target)
      : this.runService.startExecution(projectId, runId);
    return start.pipe(
      tap((gestartet) => {
        if (gestartet.nextAction === 'TRIGGER_PIPELINE') {
          this.snackBar.open(this.translate.instant('testRunExecutions.pipelineQueued'), undefined, {
            duration: MELDUNG_MS,
          });
          this.router.navigate(durchlaeufe);
          return;
        }
        this.router.navigate([...durchlaeufe, gestartet.executionId]);
      }),
      catchError((error: unknown) => {
        const { key, params } = testlaufStartFehler(error, target);
        this.snackBar.open(this.translate.instant(key, params), undefined, { duration: MELDUNG_MS });
        return EMPTY;
      }),
    );
  }

  private ladeFehler(): Observable<never> {
    this.snackBar.open(this.translate.instant('postfach.navigate.error'), undefined, {
      duration: MELDUNG_MS,
      panelClass: 'snackbar-error',
    });
    return EMPTY;
  }
}
